'use client';

import { useA11yId } from '@/hooks/useA11yId';
import clsx from 'clsx';
import { useState, type ReactNode } from 'react';
import styles from './Accordion.module.scss';

export type AccordionHeadingLevel = 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export interface AccordionItem {
  /** Stable identifier used as the React key and in the open-state set */
  id: string;
  /** Visible trigger label */
  title: ReactNode;
  /** Panel content revealed when the item is open */
  content: ReactNode;
}

/**
 * Per-part class overrides. The primitive ships behaviour and the collapse
 * mechanics only; callers pass their own CSS-module classes for type, spacing
 * and dividers — the same contract as `FolderTabs`.
 *
 * Every key lands on a DOM element (asserted in `Accordion.test.tsx`, so none
 * can silently become a no-op).
 */
export interface AccordionClassNames {
  root?: string;
  item?: string;
  /** Added to the item element while it is open — hook for open-state styling */
  itemOpen?: string;
  headingWrap?: string;
  trigger?: string;
  title?: string;
  icon?: string;
  panel?: string;
  panelInner?: string;
}

export interface AccordionProps {
  items: AccordionItem[];
  /**
   * Whether more than one item can stay open at a time. Defaults to `true`:
   * both accordion designs in this codebase (UG technical features, the live UG
   * site) keep siblings open, so single-open is the special case.
   */
  allowMultiple?: boolean;
  /** Items open on first render (uncontrolled). */
  defaultOpenIds?: string[];
  /**
   * Heading level wrapping each trigger. Callers pass one level below the
   * heading that introduces the accordion, so the page hierarchy never skips
   * a level (docs/SEO-RULES.md §14).
   */
  headingLevel?: AccordionHeadingLevel;
  /**
   * Trailing indicator. A function receives the item's open state so callers
   * can swap icons; a plain node is rendered as-is and expected to be rotated
   * by CSS, which the primitive does via `--accordion-icon-rotate`.
   */
  icon?: ReactNode | ((isOpen: boolean) => ReactNode);
  classNames?: AccordionClassNames;
  /**
   * Prefix for `data-testid` hooks: `${testIdPrefix}-accordion` on the root,
   * then `-item-{n}`, `-trigger-{n}`, `-icon-{n}`, `-panel-{n}`.
   */
  testIdPrefix?: string;
}

interface AccordionRowProps extends Pick<AccordionProps, 'headingLevel' | 'icon' | 'classNames'> {
  item: AccordionItem;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
  testId: (suffix: string) => string | undefined;
}

/**
 * One disclosure row. Split out so the trigger/panel id pair can come from
 * `useA11yId` — a hook cannot be called in a loop — instead of hand-built
 * strings.
 */
function AccordionRow({
  item,
  index,
  isOpen,
  onToggle,
  headingLevel = 'h3',
  icon,
  classNames,
  testId,
}: Readonly<AccordionRowProps>) {
  const { triggerId, contentId } = useA11yId('accordion');
  const Heading = headingLevel;

  return (
    <div
      className={clsx(classNames?.item, isOpen && styles.itemOpen, isOpen && classNames?.itemOpen)}
      data-testid={testId(`item-${index}`)}
    >
      <Heading className={clsx(styles.headingWrap, classNames?.headingWrap)}>
        <button
          type="button"
          id={triggerId}
          className={clsx(styles.trigger, classNames?.trigger)}
          aria-expanded={isOpen}
          aria-controls={contentId}
          onClick={onToggle}
          data-testid={testId(`trigger-${index}`)}
        >
          <span className={classNames?.title}>{item.title}</span>
          {icon && (
            <span
              className={clsx(styles.icon, classNames?.icon)}
              aria-hidden="true"
              data-testid={testId(`icon-${index}`)}
            >
              {typeof icon === 'function' ? icon(isOpen) : icon}
            </span>
          )}
        </button>
      </Heading>
      <section
        id={contentId}
        aria-labelledby={triggerId}
        className={clsx(styles.panel, classNames?.panel)}
        data-testid={testId(`panel-${index}`)}
      >
        <div className={classNames?.panelInner}>{item.content}</div>
      </section>
    </div>
  );
}

/**
 * Accordion primitive.
 *
 * Owns open/closed state, ARIA wiring and the collapse transition; carries no
 * visual opinion beyond that — the parts it does not style receive only the
 * caller's classes.
 *
 * Closed panels stay in the DOM (the height transition needs them) but are
 * `visibility: hidden`, which removes them from both the accessibility tree and
 * the tab order, so a link inside a closed panel is not reachable.
 *
 * @example
 * <Accordion
 *   items={[{ id: 'case', title: 'Case', content: <p>Stainless steel</p> }]}
 *   headingLevel="h3"
 *   icon={<Icon name="arrow-right" size={16} />}
 *   classNames={{ item: styles.item, trigger: styles.trigger }}
 * />
 */
export function Accordion({
  items,
  allowMultiple = true,
  defaultOpenIds,
  headingLevel,
  icon,
  classNames,
  testIdPrefix,
}: Readonly<AccordionProps>) {
  const [openIds, setOpenIds] = useState<string[]>(() => defaultOpenIds ?? []);

  if (items.length === 0) return null;

  const toggle = (id: string) => {
    setOpenIds((current) => {
      if (current.includes(id)) return current.filter((openId) => openId !== id);
      return allowMultiple ? [...current, id] : [id];
    });
  };

  const testId = (suffix: string) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : undefined);

  return (
    <div className={classNames?.root} data-testid={testId('accordion')}>
      {items.map((item, index) => (
        <AccordionRow
          key={item.id}
          item={item}
          index={index}
          isOpen={openIds.includes(item.id)}
          onToggle={() => toggle(item.id)}
          headingLevel={headingLevel}
          icon={icon}
          classNames={classNames}
          testId={testId}
        />
      ))}
    </div>
  );
}

Accordion.displayName = 'Accordion';
